import React, { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ActivityLayout, 
  ActivityTimer, 
  ActivityProgress,
  useActivityData,
  activityApiService,
  formatTime,
  generateRemarks
} from '../shared';
import { UserContext } from '../../UserContext';
import { fetchActivitiesImage } from '../fetchActivitiesImage';
import './OralActivity.css';

import SpeakImage from './speak.png';
import PlayImage from './play.png';
import GoodImage from './good.png';
import CrossImage from './cross.png';
import FinishedImage from './finished.jpeg';

const OralActivity = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activityId = searchParams.get('activityId');

  // Use shared activity data hook
  const {
    actualQuestions,
    currQuestion,
    count,
    totalQuestions,
    marksPerQuestion,
    totalMarks,
    questionNumber,
    gainedMarks,
    items,
    studentId,
    setCount,
    setQuestionNumber,
    setCurrQuestion,
    setMarksPerQuestion,
    setItems,
    setGainedMarks,
    countRef,
    totalQuestionsRef,
    progressIncrement,
    questionNumberRef,
    progressCount,
    gainedMarksRef,
    totalTimeTakenOfAllQuestions,
    totalTimeOfAllQuestions,
    completeActivityRef
  } = useActivityData(activityId);

  // Oral Activity specific state
  const [imageData, setImageData] = useState(null);
  const [answer, setAnswer] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  // Refs for Oral Activity specific elements
  const progressRef = useRef(null);
  const submitButtonRef = useRef(null);
  const goodImageRef = useRef(null);
  const crossImageRef = useRef(null);
  const finishedImageRef = useRef(null);
  const studentCurrAnswerRef = useRef('');
  const marksObtainedCurrQuestionRef = useRef(0);
  const isCorrectRef = useRef(0);

  const { userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  // Fetch image data when current question changes
  useEffect(() => {
    if (currQuestion?.questionpicture) {
      fetchActivitiesImage(setImageData, currQuestion.questionpicture);
    }
  }, [currQuestion]);

  // Timer logic
  useEffect(() => {
    let intervalId;
    if (isPlaying && !isFinished && count > 0) {
      intervalId = setInterval(() => {
        countRef.current -= 1;
        setCount(countRef.current);

        if (countRef.current <= 0) {
          studentCurrAnswerRef.current = '';
          marksObtainedCurrQuestionRef.current = 0;
          isCorrectRef.current = 0;
          submitStudentQuestionResponse();

          if (questionNumberRef.current + 1 < totalQuestions) {
            questionNumberRef.current += 1;
            setQuestionNumber(prev => prev + 1);
            setCurrQuestion(actualQuestions[questionNumberRef.current]);
            setMarksPerQuestion(actualQuestions[questionNumberRef.current]?.marks || 0);
            setCount(actualQuestions[questionNumberRef.current]?.timealloted || 0);
            countRef.current = actualQuestions[questionNumberRef.current]?.timealloted || 0;
            setItems(prev => [...prev, 0]);
            progressCount.current += progressIncrement.current;
            if (progressRef.current) {
              progressRef.current.style.width = `${progressCount.current}%`;
            }
            crossImageRef.current.style.display = 'inline-block';
            goodImageRef.current.style.display = 'none';
            
            // Reset answer and enable input for next question
            setAnswer('');
            setIsPlaying(true);
          } else {
            clearInterval(intervalId);
            setIsFinished(true);
            submitButtonRef.current.style.display = 'none';
            finishedImageRef.current.style.display = 'inline-block';
            completeActivityRef.current = 1;
            submitCompleteActivityResponse();
          }
        }
      }, 1000);
    }
    
    // Start playing when component mounts or when moving to next question
    if (!isPlaying && !isFinished) {
      setIsPlaying(true);
    }
    
    return () => clearInterval(intervalId); // Cleanup interval on unmount or dependency change
  }, [isPlaying, count, isFinished, actualQuestions, totalQuestions, questionNumber]);

  // Save question response
  const submitStudentQuestionResponse = async () => {
    totalTimeTakenOfAllQuestions.current += currQuestion?.timealloted - countRef.current;
    totalTimeOfAllQuestions.current += currQuestion?.timealloted;

    try {
      await activityApiService.saveActivityQuestion({
        Question_id: currQuestion.Question_id,
        Student_id: studentId,
        timetaken: currQuestion.timealloted - countRef.current,
        StudentAnswer: studentCurrAnswerRef.current,
        MarksObtained: marksObtainedCurrQuestionRef.current,
        TotalTime: currQuestion.timealloted,
        correct: isCorrectRef.current,
      });

      await activityApiService.saveActivity({
        Activity_id: activityId,
        Student_id: studentId,
        MarksObtained: gainedMarksRef.current,
        MarksObtainedPercentage: (gainedMarksRef.current / totalMarks) * 100,
        Remarks: generateRemarks((gainedMarksRef.current / totalMarks) * 100),
        SubmitActivityFile: '',
        TimeTaken: totalTimeTakenOfAllQuestions.current,
        TotalMarks: totalMarks,
        TotalTime: totalTimeOfAllQuestions.current < 0 ? 0 : totalTimeOfAllQuestions.current,
        Complete: completeActivityRef.current,
      });
    } catch (error) {
      console.error('Error saving response:', error);
    }
  };

  // Save complete activity
  const submitCompleteActivityResponse = async () => {
    try {
      await activityApiService.saveActivity({
        Activity_id: activityId,
        Student_id: studentId,
        MarksObtained: gainedMarksRef.current,
        MarksObtainedPercentage: (gainedMarksRef.current / totalMarks) * 100,
        Remarks: 'Good Efforts',
        SubmitActivityFile: '',
        TimeTaken: totalTimeTakenOfAllQuestions.current,
        TotalMarks: totalMarks,
        TotalTime: totalTimeOfAllQuestions.current,
        Complete: completeActivityRef.current,
      });
    } catch (error) {
      console.error('Error saving activity:', error);
    }
  };

  // Handle answer submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!answer.trim()) {
      alert('Please enter an answer before submitting!');
      return;
    }

    const isCorrect = answer.trim() === currQuestion?.correctanswer;
    studentCurrAnswerRef.current = answer;
    marksObtainedCurrQuestionRef.current = isCorrect ? marksPerQuestion : 0;
    isCorrectRef.current = isCorrect ? 1 : 0;

    if (isCorrect) {
      gainedMarksRef.current += marksPerQuestion;
      setGainedMarks(gainedMarks => gainedMarks + marksPerQuestion);
      if (goodImageRef.current) goodImageRef.current.style.display = 'inline-block';
      if (crossImageRef.current) crossImageRef.current.style.display = 'none';
    } else {
      if (goodImageRef.current) goodImageRef.current.style.display = 'none';
      if (crossImageRef.current) crossImageRef.current.style.display = 'inline-block';
    }

    setItems([...items, isCorrect ? 1 : 0]);
    progressCount.current += progressIncrement.current;
    if (progressRef.current) progressRef.current.style.width = `${progressCount.current}px`;

    submitStudentQuestionResponse();

    if (questionNumber < totalQuestions) {
      questionNumberRef.current += 1;
      setQuestionNumber(prev => prev + 1);
      setCurrQuestion(actualQuestions[questionNumberRef.current]);
      setMarksPerQuestion(actualQuestions[questionNumberRef.current]?.marks || 0);
      setCount(actualQuestions[questionNumberRef.current]?.timealloted || 0);
      countRef.current = actualQuestions[questionNumberRef.current]?.timealloted || 0;
    } else {
      setIsFinished(true);
      if (submitButtonRef.current) submitButtonRef.current.style.display = 'none';
      if (finishedImageRef.current) finishedImageRef.current.style.display = 'inline-block';
      completeActivityRef.current = 1;
      submitCompleteActivityResponse();
    }
    setAnswer('');
    setIsPlaying(false);
  };

  // Play question with text-to-speech
  const handlePlay = () => {
    if (!currQuestion?.question) return;

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    
      const utterance = new SpeechSynthesisUtterance(currQuestion?.question);
      utterance.rate = 0.6;
    
      setIsPlaying(true);
      setIsSpeaking(true);

      utterance.onend = () => {
        setIsSpeaking(false);
      };

      speechSynthesis.speak(utterance);
    } else {
      console.log('Speech synthesis not supported in this browser.');
    }
  };

  // Logout handler
  const onLogout = () => {
    fetch(`${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/logout`, {
      credentials: "include",
      method: "POST",
    });
    navigate("/");
  };

  const handleCourseSelect = (course) => {
    console.log("Selected Course:", course);
  };

  return (
    <ActivityLayout
      title="Oral Activity"
      onLogout={onLogout}
      onCourseSelect={handleCourseSelect}
    >
      <div className="MAMheading">
        <div className="MAMactivity-image">
          <img src={SpeakImage} alt="" />
        </div>

        <div className="MAMactivity-text">
          <h3>{currQuestion?.ActivityName}</h3>
        </div>
      </div>

      <div className="MAMcontainer">
        <div className="MAMbody">
          <div className="left-column">
            <div className="question-tracker">
              <p>
                Question
                <span className="question-number">#{questionNumber}</span>
              </p>
              <ActivityProgress 
                current={questionNumber}
                total={totalQuestions}
                progress={(questionNumber / totalQuestions) * 100}
              />
            </div>
            <div className="mobile-timer">
              <ActivityTimer count={count} displayFormat="box" />
            </div>
            <div className="play-area">
              <button
                className="MAMplay-question"
                onClick={handlePlay}
                disabled={!isPlaying}
              >
                <img src={PlayImage} alt="" />
              </button>
              <p>Play Question</p>
            </div>
            <div className="MAMpicture">
              {imageData ? (
                <img src={imageData} alt="Question Image" />
              ) : (
                <p></p>
              )}
            </div>
            <div class="MAOanswer-submit">
              <input
                id="MAstudent-answer"
                class="MAOanswer"
                type="text"
                ref={submitButtonRef}
                placeholder="Type Answer here..."
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && questionNumber != totalQuestions && count != 0) {
                    handleSubmit(e);
                  }
                }}
                disabled={!isPlaying}
              />
              <input
                id="MAsubmission"
                type="submit"
                ref={submitButtonRef}
                onClick={handleSubmit}
                disabled={!answer.trim() || !isPlaying}
              />
            </div>
            <div class="MAremarks">
              <div class="MAcorrect-wrong-picture">
                <img
                  id="MAremarksId"
                  src={GoodImage}
                  ref={goodImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
                <img
                  id="remarksId"
                  src={CrossImage}
                  ref={crossImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
              </div>

              <div class="MAfinished-picture">
                <img
                  id="finishedId"
                  src={FinishedImage}
                  ref={finishedImageRef}
                  alt=""
                  style={{ width: "100px", display: "none" }}
                />
              </div>
            </div>
          </div>

          <div className="right-column">
            <div className="desktop-timer">
              <ActivityTimer count={count} displayFormat="box" />
            </div>
            <div id="MAmarks-color">
              <h3 className="MAMscoreHeading">Score</h3>
              <h3 className="MAMscoreHeading" style={{ display: "flex" }}>
                <span id="MAOcurrent-score">{gainedMarks}</span> /
                <span id="MAOtotal-score">{totalMarks}</span>
              </h3>
              {items.map((item, index) => (
                <div className="MAbox">
                  <div className="MAbox1">
                    <h3>{(index + 1).toString().padStart(2, "0")}</h3>
                  </div>
                  <div className={`MAbox2${item}`}>
                    {item === 1 ? "✓" : "✗"}
                  </div>
                </div>
              ))}
            </div>
            {currQuestion?.instructions && (
              <div className="">
                <span
                  style={{
                    color: "#b8860b",
                    fontWeight: "bold",
                    cursor: "pointer",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    display: "inline-block",
                    margin: "8px 0",
                    display: "flex"
                  }}
                  onClick={() => {
                    window.alert(currQuestion?.instructions);
                  }}
                >
                  Read instruction
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ActivityLayout>
  );
};

export default OralActivity;